/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {PropsWithChildren} from 'react'
import {
  Dialog as MuiDialog, DialogProps as MuiDialogProps, IconButton,
  DialogTitle, DialogActions, DialogContent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Props for the ModalDialog component.
 */
export interface ModalDialogProps extends PropsWithChildren<MuiDialogProps> {
  /** Controls whether the dialog is open or closed */
  open: boolean

  /** Function to handle closing the dialog */
  handleClose?: () => void,

  /** Title of the dialog */
  title?: string,

  /** Actions to be displayed in the dialog's action area */
  actions?: React.ReactNode,

  disableCloseOnOutsideClick?: boolean
}

/**
 * A modal dialog component that wraps MUI's Dialog component.
 *
 * @param {ModalDialogProps} props - The properties for the ModalDialog component.
 * @returns {JSX.Element} The rendered ModalDialog component.
 */
export const ModalDialog = ({
  open,
  handleClose,
  title,
  actions = null,
  disableCloseOnOutsideClick,
  children,
  ...muiDialogProps
}: ModalDialogProps) => {
  return (
    <MuiDialog
      {...muiDialogProps}
      open={open}
      keepMounted
      onClose={()=>!disableCloseOnOutsideClick && handleClose?.()}
      fullWidth
      scroll='paper'
      PaperProps={{sx: {borderRadius: "24px", minHeight: '50vh'}}}
    >
      <DialogTitle>
        {title}
        {handleClose && (
          <IconButton
            aria-label="close"
            onClick={handleClose}
            style={{position: 'absolute', right: 8, top: 8}}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        {children}
      </DialogContent>
      {actions && <DialogActions>
        {actions}
      </DialogActions>}
    </MuiDialog>
  )
}
